import { motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';

interface Step {
  year: string;
  note: string;
  color: string;
}

interface Position {
  x: number;
  y: number;
}

interface RoadmapStepsProps {
  steps: Step[];
  onYearClick?: (year: string, index: number) => void;
}

export const RoadmapSteps: React.FC<RoadmapStepsProps> = ({ steps, onYearClick }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [positions, setPositions] = useState<Position[]>([]);

  // Calculate positions of circles
  useEffect(() => {
    if (!containerRef.current) return;

    const nodes = containerRef.current.querySelectorAll('.roadmap-circle');
    const getPositions = () => {
      const rectParent = containerRef.current!.getBoundingClientRect();
      const posArray: { x: number; y: number }[] = [];
      nodes.forEach((node) => {
        const rect = node.getBoundingClientRect();
        posArray.push({
          x: rect.left + rect.width / 2 - rectParent.left,
          y: rect.top + rect.height / 2 - rectParent.top,
        });
      });
      setPositions(posArray);
    };

    getPositions();
    window.addEventListener('resize', getPositions);
    return () => window.removeEventListener('resize', getPositions);
  }, []);

  const renderPath = (isMobile: boolean) =>
    positions.map((pos, idx) => {
      if (idx === positions.length - 1) return null;
      const next = positions[idx + 1];

      const path = isMobile
        ? `M ${pos.x} ${pos.y} C ${pos.x - 80} ${(pos.y + next.y) / 2}, ${next.x + 80} ${
            (pos.y + next.y) / 2
          }, ${next.x} ${next.y}`
        : `M ${pos.x} ${pos.y} C ${(pos.x + next.x) / 2} ${pos.y - 80}, ${(pos.x + next.x) / 2} ${
            next.y + 80
          }, ${next.x} ${next.y}`;

      return (
        <motion.path
          key={idx}
          d={path}
          stroke="#C5C9E8"
          strokeWidth="2"
          fill="transparent"
          markerEnd={`url(#arrowhead${isMobile ? 'Mobile' : ''})`}
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 1, delay: 0.2 * idx }}
        />
      );
    });

  return (
    <motion.div
      ref={containerRef}
      className="relative p-6 w-full mb-10 max-w-5xl flex flex-col md:flex-row justify-center items-center gap-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      {/* SVG Connectors - behind steps */}
      <div className="absolute inset-0 pointer-events-none z-0">
        {/* Desktop */}
        <svg className="hidden md:block w-full h-full" xmlns="http://www.w3.org/2000/svg">
          {renderPath(false)}
          <defs>
            <marker
              id="arrowhead"
              markerWidth="10"
              markerHeight="7"
              refX="5"
              refY="3.5"
              orient="auto"
              fill="#C5C9E8"
            >
              <polygon points="0 0, 10 3.5, 0 7" />
            </marker>
          </defs>
        </svg>

        {/* Mobile */}
        <svg className="block md:hidden w-full h-full" xmlns="http://www.w3.org/2000/svg">
          {renderPath(true)}
          <defs>
            <marker
              id="arrowheadMobile"
              markerWidth="10"
              markerHeight="7"
              refX="5"
              refY="3.5"
              orient="auto"
              fill="#C5C9E8"
            >
              <polygon points="0 0, 10 3.5, 0 7" />
            </marker>
          </defs>
        </svg>
      </div>

      {/* Steps (on top of connectors) */}
      <div className="relative z-10 flex flex-col md:flex-row justify-center items-center gap-8">
        {steps.map((item, idx) => {
          const isEven = idx % 2 === 0;
          const colorClass =
            item.color === 'blue'
              ? 'border-[#093FB4] text-[#093FB4]'
              : 'border-[#FFC90D] text-[#FFC90D]';

          const arrowColor = item.color === 'blue' ? 'border-[#093FB4]' : 'border-[#FFC90D]';

          return (
            <motion.div
              key={idx}
              className="flex flex-col md:flex-col items-center relative roadmap-circle"
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.3 + idx * 0.3,
                ease: 'easeOut',
                type: 'spring',
                stiffness: 100,
              }}
            >
              {isEven && (
                <p className="hidden md:block mb-4 text-gray-700 text-sm md:text-base text-center font-semibold max-w-[160px]">
                  {item.note}
                </p>
              )}

              {/* Mobile text */}
              {isEven ? (
                <p className="md:hidden absolute -left-32 top-1/2 -translate-y-1/2 text-gray-700 font-semibold text-sm max-w-[100px] text-right">
                  {item.note}
                </p>
              ) : (
                <p className="md:hidden absolute -right-32 top-1/2 -translate-y-1/2 text-gray-700 font-semibold text-sm max-w-[100px] text-left">
                  {item.note}
                </p>
              )}

              <motion.button
                onClick={() => onYearClick?.(item.year, idx)}
                whileHover={{ scale: 1.3 }}
                whileTap={{ scale: 0.97 }}
                className={`w-24 h-24 md:w-32 md:h-32 flex items-center justify-center rounded-full md:border-[25px] border-[15px] shadow-lg relative transition-transform ${colorClass}`}
              >
                {/* Arrows */}
                <div
                  className={`absolute hidden md:block ${
                    isEven ? 'md:-top-8' : 'md:-bottom-8 rotate-180'
                  } w-0 h-0 border-l-[14px] border-r-[14px] border-b-[14px] border-l-transparent border-r-transparent ${arrowColor}`}
                ></div>

                <div
                  className={`absolute md:hidden ${
                    isEven ? '-left-6 rotate-[-90deg]' : '-right-6 rotate-[90deg]'
                  } w-0 h-0 border-l-[6px] border-r-[6px] border-b-[16px] border-l-transparent border-r-transparent ${arrowColor}`}
                ></div>

                <motion.span
                  className="font-bold text-black text-sm md:text-lg"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 + idx * 0.3, duration: 0.4 }}
                >
                  {item.year}
                </motion.span>
              </motion.button>

              {!isEven && (
                <p className="hidden md:block mt-4 text-gray-700 text-sm md:text-base font-semibold text-center max-w-[160px]">
                  {item.note}
                </p>
              )}
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
};

//  <div
//         ref={containerRef}
//         className="relative p-6 w-full mb-10 max-w-5xl flex flex-col md:flex-row justify-center items-center gap-8"
//       >
//         {/* Desktop Curved Connectors */}
//         <svg
//           className="absolute top-0 left-0 w-full h-full pointer-events-none hidden md:block"
//           xmlns="http://www.w3.org/2000/svg"
//         >
//           {positions.map((pos, idx) => {
//             if (idx === positions.length - 1) return null;
//             const next = positions[idx + 1];

//             const path = `M ${pos.x} ${pos.y} C ${(pos.x + next.x) / 2} ${pos.y - 80}, ${
//               (pos.x + next.x) / 2
//             } ${next.y + 80}, ${next.x} ${next.y}`;

//             return (
//               <path
//                 key={idx}
//                 d={path}
//                 stroke="#C5C9E8"
//                 strokeWidth="2"
//                 fill="transparent"
//                 markerEnd="url(#arrowhead)"
//               />
//             );
//           })}
//           <defs>
//             <marker
//               id="arrowhead"
//               markerWidth="10"
//               markerHeight="7"
//               refX="5"
//               refY="3.5"
//               orient="auto"
//               fill="#C5C9E8"
//             >
//               <polygon points="0 0, 10 3.5, 0 7" />
//             </marker>
//           </defs>
//         </svg>

//         {/* Mobile Straight Connectors */}
//         {/* Mobile Curved Connectors */}
//         <svg
//           className="absolute top-0 left-0 w-full h-full pointer-events-none block md:hidden"
//           xmlns="http://www.w3.org/2000/svg"
//         >
//           {positions.map((pos, idx) => {
//             if (idx === positions.length - 1) return null;
//             const next = positions[idx + 1];

//             // vertical curve for mobile
//             const path = `M ${pos.x} ${pos.y} C ${pos.x - 80} ${(pos.y + next.y) / 2}, ${
//               next.x + 80
//             } ${(pos.y + next.y) / 2}, ${next.x} ${next.y}`;

//             return (
//               <path
//                 key={idx}
//                 d={path}
//                 stroke="#C5C9E8"
//                 strokeWidth="2"
//                 fill="transparent"
//                 markerEnd="url(#arrowheadMobile)"
//               />
//             );
//           })}
//           <defs>
//             <marker
//               id="arrowheadMobile"
//               markerWidth="10"
//               markerHeight="7"
//               refX="5"
//               refY="3.5"
//               orient="auto"
//               fill="#C5C9E8"
//             >
//               <polygon points="0 0, 10 3.5, 0 7" />
//             </marker>
//           </defs>
//         </svg>

//         {/* Steps */}
//         {steps.map((item, idx) => {
//           const isEven = idx % 2 === 0;
//           return (
//             <div
//               key={idx}
//               className="flex flex-col md:flex-col items-center relative roadmap-circle"
//             >
//               {/* Desktop text (top for even, bottom for odd) */}
//               {isEven && (
//                 <p className="hidden md:block mb-4 text-gray-700 text-sm md:text-base text-center font-semibold max-w-[160px]">
//                   {item.note}
//                 </p>
//               )}

//               {/* Mobile text (left for even, right for odd) */}
//               {isEven && (
//                 <p className="md:hidden absolute -left-32 top-1/2 -translate-y-1/2 text-gray-700 font-semibold text-sm max-w-[100px] text-right">
//                   {item.note}
//                 </p>
//               )}
//               {!isEven && (
//                 <p className="md:hidden absolute -right-32 top-1/2 -translate-y-1/2 text-gray-700 font-semibold text-sm max-w-[100px] text-left">
//                   {item.note}
//                 </p>
//               )}

//               {/* Circle */}
//               <div
//                 className={`w-24 h-24 md:w-32 md:h-32 flex items-center justify-center rounded-full md:border-[25px] border-[15px] shadow-lg relative ${
//                   item.color === 'blue'
//                     ? 'border-[#093FB4] text-[#093FB4]'
//                     : 'border-[#FFC90D] text-[#FFC90D]'
//                 }`}
//               >
//                 {/* Desktop Arrow */}
//                 <div
//                   className={`
//             absolute hidden md:block
//             ${isEven ? 'md:-top-8' : 'md:-bottom-8 rotate-180'}
//             w-0 h-0
//             border-l-[14px] border-r-[14px] border-b-[14px]
//             ${
//               item.color === 'blue'
//                 ? 'border-l-transparent border-r-transparent border-[#093FB4]'
//                 : 'border-l-transparent border-r-transparent border-[#FFC90D]'
//             }
//           `}
//                 ></div>

//                 {/* Mobile Arrow */}
//                 <div
//                   className={`
//             absolute md:hidden
//             ${isEven ? '-left-6 rotate-[-90deg]' : '-right-6 rotate-[90deg]'}
//             w-0 h-0
//             border-l-[6px] border-r-[6px] border-b-[16px]
//             ${
//               item.color === 'blue'
//                 ? 'border-l-transparent border-r-transparent border-[#093FB4]'
//                 : 'border-l-transparent border-r-transparent border-[#FFC90D]'
//             }
//           `}
//                 ></div>

//                 <span className="font-bold text-black text-sm md:text-lg">{item.year}</span>
//               </div>

//               {/* Desktop text (bottom for odd) */}
//               {!isEven && (
//                 <p className="hidden md:block mt-4 text-gray-700 text-sm md:text-base font-semibold text-center max-w-[160px]">
//                   {item.note}
//                 </p>
//               )}
//             </div>
//           );
//         })}
//       </div>
